/**
 * Service-admin "view as" override.
 *
 * When set, faction-scoped queries pass ?faction_id=X to the backend so the
 * admin can browse another faction's data (war page, war reports,
 * cumulative, member trend, my contributions, etc.). The backend's
 * validate_queue_access already honors this query param for admins.
 *
 * State is in-memory + localStorage, exposed via a tiny store + the
 * useAdminFaction() hook in src/hooks. Cleared automatically on logout.
 */

const STORAGE_KEY = "wooly:admin_faction_override";

export interface AdminFactionState {
  factionId: number | null;
  factionName: string | null;
}

type Listener = (s: AdminFactionState) => void;

function load(): AdminFactionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { factionId: null, factionName: null };
    const parsed = JSON.parse(raw);
    if (typeof parsed?.factionId !== "number") {
      return { factionId: null, factionName: null };
    }
    return {
      factionId: parsed.factionId,
      factionName:
        typeof parsed.factionName === "string" ? parsed.factionName : null,
    };
  } catch {
    return { factionId: null, factionName: null };
  }
}

let state: AdminFactionState = load();
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(state);
}

function persist() {
  if (state.factionId === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
}

export function getAdminFactionState(): AdminFactionState {
  return state;
}

export function subscribeAdminFaction(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function setAdminFactionOverride(
  factionId: number,
  factionName: string,
): void {
  state = { factionId, factionName };
  persist();
  emit();
}

export function clearAdminFactionOverride(): void {
  state = { factionId: null, factionName: null };
  persist();
  emit();
}

/**
 * Append ?faction_id=X to a URL when the admin override is set.
 * Used by faction-scoped queries on the war / war-reports / me pages so
 * an admin's "view as" applies consistently.
 */
export function withAdminFaction(path: string): string {
  const fid = state.factionId;
  if (fid === null) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}faction_id=${fid}`;
}
